import { Component } from '@theme/component';

/**
 * A component that handles lazy loading of videos using Intersection Observer
 * @extends {Component}
 */
class VideoLazyLoad extends Component {
  #observer = null;
  #hasLoaded = false;

  connectedCallback() {
    super.connectedCallback();
    
    // Only initialize if lazy loading is enabled
    if (!this.hasAttribute('data-lazy-load')) {
      return;
    }

    this.#setupIntersectionObserver();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cleanup();
  }

  #setupIntersectionObserver() {
    if (!('IntersectionObserver' in window)) {
      // Fallback for browsers without IntersectionObserver
      this.#loadVideo();
      return;
    }

    const options = {
      root: null,
      rootMargin: '50px', // Start loading when video is 50px away from viewport
      threshold: 0.1
    };

    this.#observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !this.#hasLoaded) {
          this.#loadVideo();
          this.#cleanup();
        }
      });
    }, options);

    this.#observer.observe(this);
  }

  #loadVideo() {
    if (this.#hasLoaded) return;
    
    this.#hasLoaded = true;
    
    // Trigger the deferred media to load
    const playButton = this.querySelector('[ref="deferredMediaPlayButton"]');
    if (playButton) {
      playButton.click();
    } else {
      // If no play button, directly load the content
      const deferredMedia = this.querySelector('deferred-media');
      if (deferredMedia && typeof deferredMedia.loadContent === 'function') {
        deferredMedia.loadContent();
      }
    }
  }

  #cleanup() {
    if (this.#observer) {
      this.#observer.disconnect();
      this.#observer = null;
    }
  }
}

if (!customElements.get('video-lazy-load')) {
  customElements.define('video-lazy-load', VideoLazyLoad);
}
